import { Annotation, isCollectionAnnotation } from "@perceptron/core/types";

/**
 * One asset's annotations, pruned out of a response that may span several.
 *
 * Every surface that renders an asset is handed a self-contained slice and has
 * no access to anything else, so a box or track cannot appear over the wrong
 * asset no matter what ids are in flight. Only the agent text reads the whole
 * response, because it narrates across assets.
 */

const NO_ANNOTATIONS: readonly Annotation[] = Object.freeze([]);

/** Slices are cached per response, and within that per asset, on array identity. */
const sliceCache = new WeakMap<readonly Annotation[], Map<number, readonly Annotation[]>>();

/**
 * This node as it exists for one asset, or undefined when nothing in it belongs
 * there.
 *
 * A collection can span assets — one `<collection mention="sailboats">` over
 * several images — so it is kept wherever it still has members and rebuilt with
 * just those. Unchanged nodes are returned as-is, so slices stay referentially
 * stable and React can skip work.
 */
function pruneToAsset(node: Annotation, assetIdx: number): Annotation | undefined {
  if (!isCollectionAnnotation(node)) {
    return node.assetIdx === assetIdx ? node : undefined;
  }
  let changed = false;
  const kept: Annotation[] = [];
  for (const child of node.children) {
    const pruned = pruneToAsset(child, assetIdx);
    if (pruned !== child) changed = true;
    if (pruned) kept.push(pruned);
  }
  if (kept.length === 0) return undefined;
  return changed ? ({ ...node, children: kept } as Annotation) : node;
}

/** The slice of `nodes` belonging to `assetIdx`; empty and stable when it has none. */
export function annotationsForAsset(nodes: readonly Annotation[], assetIdx: number): readonly Annotation[] {
  let byAsset = sliceCache.get(nodes);
  if (!byAsset) {
    byAsset = new Map();
    sliceCache.set(nodes, byAsset);
  }
  const cached = byAsset.get(assetIdx);
  if (cached) return cached;

  const slice: Annotation[] = [];
  for (const node of nodes) {
    const pruned = pruneToAsset(node, assetIdx);
    if (pruned) slice.push(pruned);
  }
  const result = slice.length ? slice : NO_ANNOTATIONS;
  byAsset.set(assetIdx, result);
  return result;
}
